import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, ShieldCheck } from "lucide-react";
import {
  hasConsentDecision, acceptAll, rejectAll, setConsent, getConsent,
} from "@/lib/cookieConsent";

/**
 * Cookie banner GDPR-compliant per visitatori della landing.
 *
 * Comportamento:
 * - Appare solo se l'utente NON ha ancora preso una decisione (localStorage vuoto)
 * - 3 azioni: Accetta tutti / Solo necessari / Personalizza
 * - Le scelte sono salvate in localStorage e rispettate dal modulo analytics
 *
 * Conforme:
 * - GDPR (Reg. UE 679/2016): consenso preventivo per cookie non strettamente necessari
 * - Provvedimento Garante Privacy 10 giugno 2021: paritarietà visiva "accetta" vs "rifiuta"
 * - Direttiva ePrivacy 2002/58/CE: il banner blocca il caricamento dei pixel marketing
 *   finché non c'è consenso (vedi `analytics.js` initAnalytics gated by hasMarketingConsent)
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [marketing, setMarketing] = useState(true);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    // Mostra solo se nessuna decisione esistente
    if (!hasConsentDecision()) {
      // Piccolo delay per non distrarre subito al landing
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    setVisible(false);
    // Reload soft per inizializzare il pixel ora che c'è consenso
    window.location.reload();
  };

  const handleRejectAll = () => {
    rejectAll();
    setVisible(false);
  };

  const handleSaveCustom = () => {
    setConsent({ marketing, analytics });
    setVisible(false);
    if (marketing) window.location.reload();
  };

  if (!visible) return null;

  // Modalità compatta di default: bottom-bar non bloccante. Si espande in card
  // solo se l'utente clicca "Personalizza" (per setting granulari).
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] pointer-events-none"
      data-testid="cookie-banner"
    >
      <div className={`mx-auto pointer-events-auto ${customizing ? "max-w-3xl p-3 sm:p-6" : "max-w-5xl px-3 pb-3 sm:px-4 sm:pb-4"}`}>
        <div className={`bg-white shadow-2xl border border-slate-200 ${customizing ? "rounded-2xl p-5 sm:p-6" : "rounded-xl px-3 py-2.5 sm:px-4 sm:py-3"}`}>
        {!customizing ? (
          <div className="flex items-center gap-3">
            <Cookie className="w-4 h-4 shrink-0" style={{ color: "var(--gold-hover)" }} />
            <p className="flex-1 text-[12px] sm:text-sm text-slate-700 leading-snug">
              Usiamo cookie tecnici e (con consenso) Meta Pixel.{" "}
              <Link to="/privacy" className="underline text-slate-600 hover:text-slate-900">Privacy</Link>
            </p>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={() => setCustomizing(true)}
                className="hidden sm:inline-flex h-8 px-2.5 rounded-md text-[12px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                data-testid="cookie-customize"
              >
                Personalizza
              </button>
              <button
                onClick={handleRejectAll}
                className="h-8 px-2.5 rounded-md text-[12px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 border border-slate-200"
                data-testid="cookie-reject-all"
              >
                Solo necessari
              </button>
              <button
                onClick={handleAcceptAll}
                className="h-8 px-3 rounded-md text-[12px] font-semibold text-white"
                style={{ background: "var(--gold-hover)" }}
                data-testid="cookie-accept-all"
              >
                Accetta
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <p className="font-display font-bold flex items-center gap-2"><ShieldCheck className="w-4 h-4" style={{ color: "var(--gold-hover)" }} /> Le tue preferenze</p>
              <button onClick={() => setCustomizing(false)} className="text-slate-400 hover:text-slate-700">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div className="rounded-md border border-slate-200 p-3 bg-slate-50">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-sm">Cookie tecnici / necessari</p>
                    <p className="text-xs text-slate-500 mt-0.5">Indispensabili per il funzionamento del portale (sessione, login). Sempre attivi.</p>
                  </div>
                  <span className="text-xs font-semibold text-slate-400 px-2 py-1 rounded bg-slate-200">Sempre attivi</span>
                </div>
              </div>
              <label className="flex items-start gap-3 cursor-pointer rounded-md border border-slate-200 p-3 hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="mt-0.5 accent-amber-600"
                  data-testid="cookie-toggle-analytics"
                />
                <div className="flex-1">
                  <p className="font-semibold text-sm">Cookie statistici (analytics)</p>
                  <p className="text-xs text-slate-500 mt-0.5">Misurano in forma anonima quante persone visitano il sito e da quale fonte. Non identificano individui specifici.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer rounded-md border border-slate-200 p-3 hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="mt-0.5 accent-amber-600"
                  data-testid="cookie-toggle-marketing"
                />
                <div className="flex-1">
                  <p className="font-semibold text-sm">Cookie di marketing (Meta Pixel)</p>
                  <p className="text-xs text-slate-500 mt-0.5">Ci permettono di mostrare annunci pertinenti su Facebook/Instagram e di misurare le conversioni delle campagne pubblicitarie.</p>
                </div>
              </label>
            </div>
            <button
              onClick={handleSaveCustom}
              className="w-full h-11 rounded-md font-semibold text-sm text-white"
              style={{ background: "var(--gold-hover)" }}
              data-testid="cookie-save-prefs"
            >
              Salva le mie preferenze
            </button>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
